import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as searchActions from '../actions/search';
import SearchFilters from '../components/searchFilters';
import classNames from 'classnames';
import { formatDate, formatMySearchesDate } from '../components/utils';
import { Modal, ModalHeader, ModalBody, Input } from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { ReactComponent as Pencil } from '../images/icons/pencil.svg';
import { ReactComponent as Trash } from '../images/icons/trash-blue.svg';
import '../scss/_mySearches.scss';
import LoginModal from './LoginModal';

const MySearches = (props) => {
    const { previousSearches = null, savedSearches = null } = props;
    const [tab, setTab] = useState(props.searchConfig ? 'filters' : 'saved');
    const hasPreviousSearch = previousSearches !== null;
    const hasSavedSearch = savedSearches !== null;


    return (
        <div className="my-searches">
            <div className="my-searches-top">
                <div className="my-searches-title">My Searches</div>
                <div className="my-searches-tabs">
                    {props.searchConfig &&
                        <button ref={props.setRef} className={classNames("btn btn-link", { 'active': tab === 'filters' })}
                            onClick={() => setTab('filters')}>Search Filters</button>
                    }
                    {hasSavedSearch && <button className={classNames("btn btn-link", { 'active': tab === 'saved' })}
                        onClick={() => setTab('saved')}>Saved Searches</button>}
                    {hasPreviousSearch && <button className={classNames("btn btn-link", { 'active': tab === 'previous' })}
                        onClick={() => setTab('previous')}>Previous Searches</button>}
                    <button className="btn btn-link new-search"
                        onClick={() => props.history.push({ pathname: '/search', openFilters: true })}>
                        {props.isMobile ? '+ New' : '+ New Search'}
                    </button>
                </div>
            </div>
            <div className="my-searches-body">
                {tab === 'filters' &&
                    <SearchFilters {...props} />
                }
                {tab === 'previous' && hasPreviousSearch && previousSearches.map((search, i) => {
                    if (!search.criteria) return null
                    return <div className="previous-search" key={i}>
                        <button className="btn btn-link" onClick={() => props.loadSearch(search)}>
                            {formatMySearchesDate(search.updated)}{search.criteria.stories ? ` - ${search.criteria.stories}` : ''}
                        </button>
                        <div className="criteria">{displayCriteria(search)}</div>
                    </div>
                })}
                {tab === 'saved' && hasSavedSearch && savedSearches.map((search, i) => {
                    return <div className="saved-search" key={i}>
                        <div className="saved-search-top d-inline-flex w-100">
                            <button className="btn btn-link" onClick={() => props.loadSearch(search)}>
                                {search.searchName}
                            </button>
                            <SavedSearchModal
                                search={search}
                                savedSearchNames={savedSearches ? savedSearches.map(x => x.searchName) : []}
                                savedSearchesCount={(savedSearches || []).length}
                            />
                        </div>
                        <div className="criteria d-inline-flex w-100">
                            {displayCriteria(search)}
                        </div>
                        <div className="pd-lft-10 mg-btm-10">Created {formatMySearchesDate(search.updated)}</div>
                    </div>
                })}
            </div>
        </div>
    )
}

export default MySearches

export const SavedSearchModal = (props) => {
    const { search } = props;
    const [open, setOpen] = useState(null);
    const [name, setName] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false)
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.session.isAuthenticated);

    const toggle = (e) => {
        if (!isAuthenticated) {
            return setShowLoginModal(!showLoginModal)
        }

        if (open) // closing modal without save - clear name
            setName('');

        setOpen(!open);
    }

    useEffect(() => {
        if (props.search.id)
            setName(props.search.searchName);
    }, [props.search])

    const deleteSearch = () => {
        let searchId = search.id;
        setOpen(false);
        confirm({
            title: 'Are you sure you want to delete?',
            message: name + ' will be deleted',
            confirmColor: 'danger',
            confirmText: 'Delete',
            cancelText: 'Cancel'
        }).then(res => {
            if (res)
                dispatch(searchActions.deleteSearch(searchId));
        });
    }

    const saveSearch = () => {
        if (!name.trim())
            confirm({
                title: 'Invalid Name',
                message: 'Please give your search a name',
                confirmColor: 'error',
                confirmText: 'Ok',
                cancelText: null
            });
        else if (props.savedSearchesCount >= 10 && !search.id)
            confirm({
                title: 'Saved Searches Limit Reached',
                message: 'You already have 10 saved searches. If you want to save another search, please delete one of your existing searches.',
                confirmColor: 'error',
                confirmText: 'Ok',
                cancelText: null
            });
        else if (props.savedSearchNames.includes(name))
            confirm({
                title: "Saved Search '" + name + "' Already Exists",
                message: 'Please choose a unique name for this search.',
                confirmColor: 'error',
                confirmText: 'Ok',
                cancelText: null
            });
        else {
            dispatch(searchActions.saveSearch({
                ...search,
                name: name,
            }));
            setOpen(false);
        }
    }

    return (
        <>
            <LoginModal {...props} open={showLoginModal} onClose={() => setShowLoginModal(!showLoginModal)} />

            <div className="saved-search-modal">
                {props.isSaveSearchBtn &&
                    <button id="saveSearch" className="search-btn save-search"
                        onClick={toggle}>
                        Save Search
                    </button>
                }
                {!props.isSaveSearchBtn &&
                    <span className="d-flex flex-grow-1">
                        <Pencil onClick={toggle} />
                        <Trash className="mg-lft-auto mg-rt-20" onClick={deleteSearch} />
                    </span>
                }
                <Modal centered size="md" isOpen={open} toggle={toggle} className='modal_close_button dyna-modal save-search-modal'>
                    <ModalHeader toggle={toggle} charCode={<svg className='cross' />} >
                        <div>
                            <h4>{search.id ? 'Edit Saved Search' : 'Save Search'}</h4>
                            <div className="dyna-divider dyna-divider-darkbg"></div>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <div className="align-items-start d-flex flex-column mg-lft-auto">
                            <div className="ft-sz-14">Search Name</div>
                            <div className="w-100">
                                <Input value={name} type="text" placeholder="Save search name"
                                    onChange={(e) => setName(e.target.value)} />
                                <span className="criteria">{displayCriteria(search)}</span>
                            </div>
                            <div className="d-flex justify-content-between mg-top-24 w-100">
                                <button className={`btn btn-link${!search.id ? ' invisible' : ''}`} onClick={deleteSearch}>Delete this Search</button>
                                <button className="dyna-btn" onClick={saveSearch}>{search.id ? 'Update' : 'Save'}</button>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        </>
    )
}

const displayCriteria = (search) => {
    let criteria = [];
    let prices = [{ min: 'minRent', max: 'maxRent' }, { min: 'minForSale', max: 'maxForSale' }, { min: 'minCommercial', max: 'maxCommercial' }];

    if (!search.criteria) return '';

    if (search.criteria.forSale)
        criteria.push('For Sale');
    else
        criteria.push('For Rent');

    criteria.push(search.criteria.status);

    if (search.criteria.propertyType && search.criteria.propertyType.length > 0)
        criteria = criteria.concat(search.criteria.propertyType);

    if (search.criteria.minBeds)
        criteria.push(search.criteria.minBeds + '+ bedrooms')

    if (search.criteria.minBaths)
        criteria.push(search.criteria.minBaths + '+ bathrooms')

    prices.forEach((price) => {
        if (search.criteria[price.min] && search.criteria[price.max])
            criteria.push(`${search.criteria[price.min]}-${search.criteria[price.max]}`);
        else if (search.criteria[price.min])
            criteria.push('Min ' + search.criteria[price.min]);
        else if (search.criteria[price.max])
            criteria.push('Max ' + search.criteria[price.max])
    })

    if (search.criteria.cities && search.criteria.cities.length > 0)
        criteria = criteria.concat(search.criteria.cities);
    if (search.criteria.addresses && search.criteria.addresses.length > 0)
        criteria = criteria.concat(search.criteria.addresses);

    if (search.criteria.zipCodes && search.criteria.zipCodes.length > 0)
        criteria = criteria.concat(search.criteria.zipCodes);

    if (search.criteria.hasPool === false) {
        criteria = criteria.concat('No Pool');
    }

    // begin adding rest of fields
    criteria.push(minMax(search, 'minLotSize', 'maxLotSize'));
    criteria.push(minMax(search, 'minSqft', 'maxSqft'));
    criteria.push(minMax(search, 'minClosedDate', 'maxClosedDate', (val) => formatDate(val)));
    criteria.push(minMax(search, 'minYearBuilt', 'maxYearBuilt'));
    criteria.push(minMax(search, 'minOpenHouseDate', 'maxOpenHouseDate'));
    criteria.push(minMax(search, null, 'maxHOA'));
    criteria.push(minMax(search, 'minParkingSpaces', null));
    criteria.push(minMax(search, 'minGarageSpaces', null));
    criteria.push(minMax(search, 'minParkingGarageSpaces', null));
    criteria.push(withLabel(search, 'stories'));
    criteria.push(withLabel(search, 'subdivisions'));
    ['includeOpenHouse', 'mustHaveView', 'hasPool', 'mapbound', 'polygon', 'mustHaveWaterfrontView', 'mustHaveBasement', 'mustHaveParkingSpaces'].forEach(f => {
        if (search.criteria[f])
            criteria.push(filtersText[f]);
    });
    criteria.push(withLabel(search, 'sortOrder'));

    return criteria.filter(c => c).join(', ');
}

const minMax = (search, min, max, format) => {
    let minVal = search.criteria[min];
    let maxVal = search.criteria[max];
    if (format) {
        minVal = format(minVal);
        maxVal = format(maxVal);
    }
    if (minVal && filtersText[min] !== 'No min' && maxVal && filtersText[max] !== 'No max')
        return `${minVal}-${maxVal} ${filtersText[min]}`;
    else if (minVal && minVal !== 'No min')
        return 'Min ' + minVal + ' ' + filtersText[min];
    else if (minVal)
        return minVal + ' ' + filtersText[min];
    else if (maxVal && maxVal === 'No HOA')
        return maxVal;
    else if (maxVal && maxVal !== 'No max')
        return 'Max ' + maxVal + ' ' + filtersText[max];
    else if (maxVal)
        return maxVal + ' ' + filtersText[max];

    return null;
}

const withLabel = (search, key, showLabel = true) => {
    if (search.criteria[key]) {
        if (showLabel)
            return `${search.criteria[key]} ${filtersText[key]}`
        else
            return filtersText[key];
    }
    else return null;
}

const filtersText = {
    includeOpenHouse: 'Open House',
    maxHOA: 'HOA',
    maxLotSize: 'Lot Size',
    maxOpenHouseDate: 'Open House',
    maxSqft: 'SqFt',
    maxYearBuilt: 'Year Built',
    minLotSize: 'Lot Size',
    minOpenHouseDate: 'Open House',
    minSqft: 'Sqft',
    minYearBuilt: 'Year Built',
    minClosedDate: 'Closed Date',
    maxClosedDate: 'Closed Date',
    status: 'Status',
    minParkingSpaces: 'Parking Spaces',
    minGarageSpaces: 'Garage Spaces',
    hasPool: 'Has Pool',
    mustHaveView: 'Must Have View',
    stories: 'Stories',
    keywords: 'Keywords',
    zipCodes: 'Zip Codes',
    subdivisions: 'Subdivisions',
    mapbound: 'Mapbound',
    sortOrder: 'Sort Order',
    minParkingGarageSpaces: 'Parking Garage Spaces',
    mustHaveWaterfrontView: ' Waterfront Only',
    polygon: 'Polygon',
    mustHaveBasement: 'Must Have Basement',
    mustHaveParkingSpaces: 'Must Have Parking Spaces'
}
